import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Sparkle, ArrowRight, X, CheckCircle, Robot, Waveform, ShieldCheck, PlayCircle,
} from "@phosphor-icons/react";

/**
 * AIFirstLandingStrip — full-width dark strip below the landing hero.
 *
 * Left column: punchy marketing pitch + CTA to open the waitlist modal.
 * Right column: an animated product mockup (framer-motion driven) that
 * loops through:
 *   1. Candidate's face-in-frame browser recorder
 *   2. AI interview transcript filling in
 *   3. Rubric scores animating up
 *   4. "Shortlisted → forwarded to HM round" confetti moment
 *
 * The animation runs on CSS/JS only — no real video file, no bandwidth
 * cost, no vendor lock. When the real V1.4 product ships, swap this
 * component for an actual screen recording without touching the strip.
 */
export default function AIFirstLandingStrip() {
    const [showModal, setShowModal] = useState(false);
    return (
        <>
            <section
                data-testid="ai-first-landing-strip"
                className="relative bg-ink text-white overflow-hidden border-y border-white/10"
            >
                {/* Splash gradient accent bar */}
                <span
                    aria-hidden
                    className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-splash via-cobalt to-splash"
                />
                {/* Subtle radial glow behind mockup */}
                <span
                    aria-hidden
                    className="absolute top-1/2 right-[10%] -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-splash/10 blur-[120px] pointer-events-none"
                />

                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
                    {/* --- Left column: pitch --- */}
                    <div>
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.24em] px-2.5 py-1 bg-gradient-to-r from-splash to-cobalt text-ink">
                            <Sparkle size={11} weight="fill" /> Coming soon · AI-First plan
                        </span>
                        <h2 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-5 leading-[0.95]">
                            Let AI take the<br />
                            <span className="bg-gradient-to-r from-splash to-cobalt bg-clip-text text-transparent">
                                boring interviews.
                            </span>
                        </h2>
                        <p className="text-base sm:text-lg text-white/70 mt-4 leading-relaxed max-w-lg">
                            NaukriDaddy's next release. Candidates self-book, AI runs the screening
                            round like a human, and only the top 20% reach your calendar.
                        </p>

                        <ul className="mt-6 space-y-2.5 max-w-md">
                            <li className="flex items-start gap-2.5 text-sm">
                                <Robot size={16} weight="bold" className="text-splash mt-0.5 shrink-0" />
                                <span className="text-white/90"><strong>Async AI video interview</strong> — auto-graded, transcript in your dashboard</span>
                            </li>
                            <li className="flex items-start gap-2.5 text-sm">
                                <Waveform size={16} weight="bold" className="text-splash mt-0.5 shrink-0" />
                                <span className="text-white/90"><strong>Voice AI interviewer</strong> — real-time conversation, no candidate friction</span>
                            </li>
                            <li className="flex items-start gap-2.5 text-sm">
                                <ShieldCheck size={16} weight="bold" className="text-splash mt-0.5 shrink-0" />
                                <span className="text-white/90"><strong>DPDPA-compliant</strong> — recruiter has final approval, always</span>
                            </li>
                        </ul>

                        <div className="mt-7 flex flex-wrap items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setShowModal(true)}
                                data-testid="ai-first-strip-cta"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-splash to-cobalt text-ink hover:opacity-90 px-6 py-3.5 text-xs font-black uppercase tracking-wider transition-opacity"
                            >
                                Join the waitlist <ArrowRight size={13} weight="bold" />
                            </button>
                            <p className="text-[11px] text-white/50">
                                Early access + launch discount for waitlist members.
                            </p>
                        </div>
                    </div>

                    {/* --- Right column: animated product mockup --- */}
                    <AIInterviewMockup />
                </div>
            </section>

            {showModal && (
                <AIFirstWaitlistModal onClose={() => setShowModal(false)} />
            )}
        </>
    );
}


/* ============================================================
 * AIInterviewMockup — the animated "trailer" (no real video).
 * Loops indefinitely so first-time visitors always see motion.
 * ============================================================ */
function AIInterviewMockup() {
    return (
        <div className="relative">
            <div className="relative bg-white/[0.03] border border-white/10 backdrop-blur-sm rounded-none p-3 sm:p-4 shadow-2xl overflow-hidden">
                {/* Fake browser chrome */}
                <div className="flex items-center gap-1.5 mb-3 pl-1">
                    <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                    <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                    <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
                    <span className="ml-3 text-[10px] font-mono text-white/40">interview.naukridaddy.com</span>
                </div>

                {/* Candidate face-in-frame area */}
                <div className="relative aspect-[4/3] bg-gradient-to-br from-[#0F172A] to-[#1E293B] border border-white/10 overflow-hidden">
                    {/* Fake candidate silhouette */}
                    <motion.div
                        initial={{ opacity: 0.6 }}
                        animate={{ opacity: [0.6, 1, 0.6] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute inset-0 flex items-center justify-center"
                    >
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-white/10 to-white/5 border border-white/20 flex items-center justify-center">
                            <span className="text-3xl">🧑‍💼</span>
                        </div>
                    </motion.div>

                    {/* REC indicator */}
                    <motion.div
                        animate={{ opacity: [1, 0.3, 1] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                        className="absolute top-3 left-3 flex items-center gap-1.5 bg-[#DC2626]/90 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-white"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-white" /> Rec
                    </motion.div>

                    {/* Question banner (types out) */}
                    <div className="absolute bottom-3 left-3 right-3 bg-black/60 backdrop-blur-sm border border-white/10 px-3 py-2">
                        <p className="text-[9px] font-black uppercase tracking-wider text-splash mb-0.5">
                            Question 3 of 8
                        </p>
                        <TypingLine text="Tell me about a system you designed that handled 1M+ requests/day…" />
                    </div>

                    {/* Anti-cheat face-detect pulse */}
                    <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-3 right-3 flex items-center gap-1 bg-emerald-500/20 border border-emerald-500/40 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-emerald-400"
                    >
                        <span className="w-1 h-1 rounded-full bg-emerald-400" /> Face ok
                    </motion.div>
                </div>

                {/* Rubric scores animating in */}
                <div className="mt-3 grid grid-cols-2 gap-2">
                    <RubricRow label="Role fit" target={22} delay={0.4} />
                    <RubricRow label="Communication" target={20} delay={0.8} />
                    <RubricRow label="Technical depth" target={21} delay={1.2} />
                    <RubricRow label="Culture" target={19} delay={1.6} />
                </div>

                {/* Result chip */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.4, duration: 0.4 }}
                    className="mt-3 flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 px-3 py-2"
                >
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400">
                        AI recommendation
                    </span>
                    <span className="text-xs font-black tracking-tight text-emerald-300 inline-flex items-center gap-1.5">
                        <CheckCircle size={13} weight="fill" /> Shortlisted — forward to HM
                    </span>
                </motion.div>
            </div>
        </div>
    );
}

/* ============================================================
 * TypingLine — simulates AI typing out an interview question
 * ============================================================ */
function TypingLine({ text }) {
    return (
        <p className="text-[11px] font-mono text-white/90 leading-snug min-h-[16px]">
            <motion.span
                key={text}
                initial={{ width: 0 }}
                animate={{ width: "100%" }}
                transition={{ duration: 3.5, repeat: Infinity, repeatDelay: 1.5, ease: "linear" }}
                className="inline-block overflow-hidden whitespace-nowrap align-bottom"
                style={{ maxWidth: "100%" }}
            >
                {text}
            </motion.span>
            <motion.span
                animate={{ opacity: [1, 0, 1] }}
                transition={{ duration: 0.9, repeat: Infinity }}
                className="inline-block w-1.5 h-3 bg-splash ml-0.5 align-middle"
            />
        </p>
    );
}

/* ============================================================
 * RubricRow — animated progress bar with numeric label
 * ============================================================ */
function RubricRow({ label, target, delay }) {
    const max = 25;
    return (
        <div className="bg-white/[0.03] border border-white/10 px-2.5 py-1.5">
            <div className="flex items-center justify-between mb-1">
                <span className="text-[9px] font-bold uppercase tracking-wider text-white/60">{label}</span>
                <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: delay + 0.5, duration: 0.3 }}
                    className="text-[10px] font-black tracking-tighter text-splash"
                >
                    {target}/25
                </motion.span>
            </div>
            <div className="relative h-1 bg-white/10 overflow-hidden">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(target / max) * 100}%` }}
                    transition={{ delay, duration: 0.7, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-splash to-cobalt"
                />
            </div>
        </div>
    );
}


/* ============================================================
 * AIFirstWaitlistModal — inline modal, reuses same POST endpoint
 * ============================================================ */
function AIFirstWaitlistModal({ onClose }) {
    const [email, setEmail] = useState("");
    const [teamSize, setTeamSize] = useState("6-20");
    const [saving, setSaving] = useState(false);
    const [joined, setJoined] = useState(false);

    const submit = async () => {
        const trimmed = (email || "").trim();
        if (!trimmed.includes("@")) {
            toast.error("Enter a valid email so we can notify you at launch.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/ai-first/waitlist", {
                email: trimmed,
                team_size: teamSize,
                interest_tier: "ai_first",
                source: "landing_page",
            });
            setJoined(true);
            toast.success(
                data?.already_on_list
                    ? "You're already on the AI-First waitlist."
                    : "You're on the list. We'll email you at launch."
            );
        } catch (ex) {
            toast.error(ex?.response?.data?.detail || "Couldn't sign you up. Try again in a moment.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="ai-first-waitlist-modal"
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="relative bg-ink text-white border border-white/10 w-full max-w-md overflow-hidden"
            >
                <span
                    aria-hidden
                    className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-splash via-cobalt to-splash"
                />
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="ai-first-modal-close"
                    className="absolute top-3 right-3 text-white/60 hover:text-white"
                >
                    <X size={18} weight="bold" />
                </button>

                {joined ? (
                    <div
                        data-testid="ai-first-modal-success"
                        className="p-8 flex flex-col items-center justify-center text-center min-h-[280px]"
                    >
                        <CheckCircle size={52} weight="fill" className="text-splash" />
                        <p className="font-heading font-black text-xl tracking-tight text-white mt-4">
                            You're on the list.
                        </p>
                        <p className="text-sm text-white/70 mt-2 max-w-xs leading-relaxed">
                            We'll email you when the AI-First plan launches — expect early access and a launch discount.
                        </p>
                        <button
                            type="button"
                            onClick={onClose}
                            className="mt-6 text-[11px] font-black uppercase tracking-wider text-white/70 hover:text-white transition-colors"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <div className="p-6 sm:p-8">
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.24em] px-2.5 py-1 bg-gradient-to-r from-splash to-cobalt text-ink">
                            <Sparkle size={11} weight="fill" /> Coming soon
                        </span>
                        <h3 className="font-heading font-black text-2xl tracking-tighter mt-4">
                            Join the AI-First waitlist.
                        </h3>
                        <p className="text-sm text-white/70 mt-2 leading-relaxed">
                            Be the first to try our AI screening + auto-shortlisting. Zero commitment.
                        </p>

                        <div className="mt-5 space-y-3">
                            <div>
                                <label
                                    htmlFor="ai-first-modal-email"
                                    className="block text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1.5"
                                >
                                    Work email
                                </label>
                                <input
                                    id="ai-first-modal-email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@agency.com"
                                    autoFocus
                                    data-testid="ai-first-modal-email-input"
                                    className="w-full bg-white/10 border border-white/20 px-3 py-2.5 text-sm text-white placeholder-white/40 focus:outline-none focus:border-splash"
                                />
                            </div>

                            <div>
                                <label
                                    htmlFor="ai-first-modal-team"
                                    className="block text-[10px] font-bold uppercase tracking-wider text-white/60 mb-1.5"
                                >
                                    Team size
                                </label>
                                <select
                                    id="ai-first-modal-team"
                                    value={teamSize}
                                    onChange={(e) => setTeamSize(e.target.value)}
                                    data-testid="ai-first-modal-team-select"
                                    className="w-full bg-white/10 border border-white/20 px-3 py-2.5 text-sm text-white focus:outline-none focus:border-splash"
                                >
                                    <option value="1-5">1–5 recruiters</option>
                                    <option value="6-20">6–20 recruiters</option>
                                    <option value="21-50">21–50 recruiters</option>
                                    <option value="50+">50+ recruiters</option>
                                </select>
                            </div>

                            <button
                                type="button"
                                onClick={submit}
                                disabled={saving}
                                data-testid="ai-first-modal-submit-btn"
                                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-splash to-cobalt text-ink hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed px-5 py-3 text-xs font-black uppercase tracking-wider transition-opacity"
                            >
                                {saving ? "Adding you…" : (
                                    <>
                                        Notify me at launch <ArrowRight size={13} weight="bold" />
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </motion.div>
        </div>
    );
}
